import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "About Us", path: "/about" },
    { name: "I'm a Patient", path: "/patient" },
    { name: "I'm a Caregiver", path: "/caregiver" },
    { name: "Join the community", path: "/register" },
    { name: "Reviews", path: "/reviews" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => !path.includes("#") && location.pathname === path;

  return (
    <nav
      className={`sticky top-0 z-40 transition-all duration-200 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-[var(--light-border)] shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="container-main">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex flex-col items-start shrink-0 group leading-none" data-testid="logo-link">
            <span className="flex items-baseline gap-1.5">
              <span className="brand-wordmark text-[var(--navy)] text-3xl md:text-4xl">
                MediTalk
              </span>
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--lime-dark)] group-hover:scale-125 transition-transform" />
            </span>
            <span className="italic font-medium text-[13px] md:text-[15px] text-[var(--charcoal)] mt-1 tracking-[0.14em]">
              By Medicys<span className="text-[0.55em] align-top tracking-normal ml-px" aria-label="registered trademark">®</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-['Outfit'] font-bold text-[18px] tracking-tight transition-colors ${
                  isActive(link.path)
                    ? "text-[#0f1422]"
                    : "text-[#475569] hover:text-[#0f1422]"
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/register"
              className="btn-teal text-sm"
              data-testid="nav-join-btn"
            >
              Get started
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-[var(--navy)]"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`mobile-menu ${isOpen ? "open" : ""} lg:hidden`}
        data-testid="mobile-menu"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="flex flex-col items-start leading-none" onClick={() => setIsOpen(false)}>
              <span className="flex items-baseline gap-1.5">
                <span className="brand-wordmark text-white text-3xl">
                  MediTalk
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--lime)]" />
              </span>
              <span className="italic font-medium text-[13px] text-white/80 mt-1 tracking-[0.14em]">
                By Medicys<span className="text-[0.55em] align-top tracking-normal ml-px" aria-label="registered trademark">®</span>
              </span>
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-white"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`font-['Outfit'] font-bold text-lg py-3 border-b border-[var(--dark-border)] ${
                  isActive(link.path) ? "text-[var(--lime)]" : "text-white"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/register"
              onClick={() => setIsOpen(false)}
              className="btn-primary text-center mt-4"
            >
              Get started
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};
